import React, { useId } from "react"
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts"
import { faChartSimple } from "@fortawesome/free-solid-svg-icons"
import { useTranslation } from "react-i18next"
import { convertToLocalMoney } from "../../../Objects/Generics/Helps"
import { FinancialChartCard } from "../../CashFlow/components/FinancialChartCard"
import { FinancialEmptyState } from "../../CashFlow/components/FinancialEmptyState"
import { usePrefersReducedMotion } from "../../CashFlow/hooks/usePrefersReducedMotion"
import {
  FINANCIAL_CHART_RESIZE_DEBOUNCE_MS,
  hasFinancialChartData,
  sanitizeFinancialSeries,
} from "../../CashFlow/utils/financialChartUtils"

interface SeriesChartProps {
  title: string
  data: { label: string; value: number }[]
  money?: boolean
}

export const SeriesChart: React.FC<SeriesChartProps> = ({ title, data, money = false }) => {
  const { t } = useTranslation("pages")
  const gradientId = `adminSeriesGradient${useId().replace(/:/g, "")}`
  const prefersReducedMotion = usePrefersReducedMotion()
  const chartData = sanitizeFinancialSeries(data)
  const chartHeight = Math.min(560, Math.max(340, chartData.length * 46 + 92))
  const formatValue = (value: number) => (money ? convertToLocalMoney(value) : value.toLocaleString("pt-BR"))

  return (
    <FinancialChartCard title={title} icon={faChartSimple}>
      {!hasFinancialChartData(chartData) ? (
        <FinancialEmptyState title={t("cashflow.chart_empty_title")} compact />
      ) : (
        <div className="w-full min-w-0" style={{ height: chartHeight }} role="img" aria-label={title}>
          <ResponsiveContainer width="100%" height="100%" debounce={FINANCIAL_CHART_RESIZE_DEBOUNCE_MS}>
            <BarChart
              data={chartData}
              layout="vertical"
              margin={{ top: 8, right: 28, left: 12, bottom: 4 }}
              accessibilityLayer
            >
              <defs>
                <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#fed7aa" />
                  <stop offset="45%" stopColor="#fb923c" />
                  <stop offset="100%" stopColor="#ea580c" />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 5" horizontal={false} />
              <XAxis
                type="number"
                tickFormatter={(value: number) => formatValue(value)}
                tick={{ fill: "#64748b", fontSize: 12, fontWeight: 600 }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                type="category"
                dataKey="label"
                width={140}
                tick={{ fill: "#334155", fontSize: 12, fontWeight: 700 }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(value: string) => (value.length > 18 ? `${value.slice(0, 17)}…` : value)}
              />
              <Tooltip
                formatter={(value) => [formatValue(Number(value ?? 0)), title]}
                cursor={{ fill: "#fff7ed" }}
                contentStyle={{
                  borderRadius: 12,
                  borderColor: "#e2e8f0",
                  boxShadow: "0 10px 30px rgba(15,23,42,.10)",
                }}
              />
              <Bar
                dataKey="value"
                name={title}
                radius={[0, 10, 10, 0]}
                fill={`url(#${gradientId})`}
                barSize={22}
                isAnimationActive={!prefersReducedMotion}
                animationDuration={850}
                animationEasing="ease-out"
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </FinancialChartCard>
  )
}
